import { useState } from "react";
import { motion } from "framer-motion";
import { Handshake, X, CheckCircle2, Printer, ArrowLeft } from "lucide-react";

const PARTNER_RULES = [
  {
    title: "Build trust before asking for money",
    text: "Keep your promises, tell the truth, and be consistent. A partner needs to believe that you will do what you say.",
    review: "We discussed trust, promises, honesty, consistency, and how each partner will prove reliability."
  },
  {
    title: "Understand what your partner wants",
    text: "Learn their goals, problems, strengths, financial expectations, and fears. Explain how working with you helps them succeed.",
    review: "We discussed goals, problems, strengths, financial expectations, fears, and expected benefits."
  },
  {
    title: "Listen more than you speak",
    text: "Ask questions, let the other person finish, and understand their point of view before proposing a solution.",
    review: "We agree to ask questions, listen fully, and understand each other before proposing solutions."
  },
  {
    title: "Make the deal fair for both sides",
    text: "Agree on each person's investment, responsibilities, time, risks, decision-making authority, and share of profits.",
    review: "We reviewed investment, responsibilities, time, risks, decision-making authority, and profit sharing."
  },
  {
    title: "Put everything in writing",
    text: "Use a written agreement covering ownership, expenses, profits, responsibilities, disputes, and how either partner can exit.",
    review: "We agree to document ownership, expenses, profits, responsibilities, disputes, and exit terms."
  },
  {
    title: "Communicate regularly",
    text: "Hold regular meetings, share progress, disclose problems early, and never hide important business information.",
    review: "We agree on regular meetings, progress updates, early problem disclosure, and transparent information sharing."
  },
  {
    title: "Protect the relationship and the business",
    text: "Keep accurate records, separate business and personal money, establish spending limits, and verify important transactions.",
    review: "We agree to accurate records, separate business/personal money, spending limits, and transaction verification."
  },
  {
    title: "Complement each other's strengths",
    text: "A strong partnership combines different skills. One person might handle sales and relationships while the other manages operations or finances.",
    review: "We identified each partner's strengths and agreed how responsibilities will use those strengths."
  },
  {
    title: "Handle disagreements respectfully",
    text: "Focus on the problem, not the person's character. Use facts, discuss options, and agree on how you will make difficult decisions.",
    review: "We agree to focus on facts and problems, discuss options respectfully, and follow an agreed decision process."
  },
  {
    title: "Think long-term",
    text: "Do not sacrifice years of trust for a small short-term gain. A good reputation and reliable relationships create future opportunities.",
    review: "We agree to protect long-term trust, reputation, and future opportunities over short-term gain."
  }
];

const blankLine = "border-b border-slate-400 min-w-0 flex-1 h-7 bg-transparent outline-none";

const PartnerGuideModal = ({ onClose }) => {
  const [view, setView] = useState("guide");
  const [checked, setChecked] = useState(() => Array(PARTNER_RULES.length).fill(false));

  const toggleRule = (index) => {
    setChecked(prev => prev.map((value, i) => i === index ? !value : value));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm no-print"
      onClick={onClose}
      data-testid="partner-guide-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="trade-card partner-agreement-shell rounded-sm p-6 w-full max-w-4xl max-h-[88vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6 no-print">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center flex-shrink-0">
              <Handshake className="w-5 h-5 trade-gold" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] trade-gold">Shipping Partner Note</p>
              <h2 className="text-2xl font-serif font-bold" data-testid="partner-guide-title">
                {view === "guide" ? "How to Win a Business Partner" : "Partnership Agreement Template"}
              </h2>
              <p className="text-xs text-white/45 mt-1">
                {view === "guide" ? "Build trust, communicate clearly, negotiate fairly, and protect the partnership" : "Review, check, print, and sign together"}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-white/40 hover:text-white" data-testid="partner-guide-close">
            <X size={20} />
          </button>
        </div>

        {view === "guide" ? (
          <>
            <p className="text-sm text-white/65 leading-relaxed mb-6">
              If you want to win a business partner, earn their trust, and build a profitable business together, the most important skill is learning how to build trust, communicate clearly, negotiate fairly, and protect the partnership. Read and practice these 10 golden rules.
            </p>
            <div className="space-y-3 mb-6" data-testid="partner-guide-rules">
              {PARTNER_RULES.map((rule, index) => (
                <div key={rule.title} className="flex gap-3 p-4 bg-white/5 border border-white/10 rounded-sm" data-testid={`partner-rule-${index + 1}`}>
                  <div className="w-8 h-8 rounded-sm bg-[#D4AF37]/10 trade-gold flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 size={16} />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-white/35 mb-1">Rule {index + 1}</p>
                    <h3 className="font-serif font-semibold text-lg mb-1">{rule.title}</h3>
                    <p className="text-sm text-white/65 leading-relaxed">{rule.text}</p>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => setView("agreement")}
              className="trade-btn-primary rounded-sm h-11 px-6 w-full sm:w-auto flex items-center justify-center gap-2"
              data-testid="agreement-template-btn"
            >
              <Printer size={16} /> Open Printable Agreement
            </button>
          </>
        ) : (
          <>
            <div className="flex flex-wrap gap-3 mb-5 no-print">
              <button onClick={() => setView("guide")} className="bg-white/10 text-white hover:bg-white/20 rounded-sm h-10 px-4 text-sm flex items-center gap-2" data-testid="agreement-back-btn">
                <ArrowLeft size={15} /> Back to Rules
              </button>
              <button onClick={() => window.print()} className="trade-btn-primary rounded-sm h-10 px-5 text-sm flex items-center gap-2" data-testid="agreement-print-btn">
                <Printer size={15} /> Print Checklist
              </button>
            </div>

            <div className="partner-agreement-print bg-white text-slate-900 rounded-sm p-6 md:p-8" data-testid="partner-agreement-template">
              <div className="text-center mb-7">
                <p className="text-[10px] uppercase tracking-[0.3em] text-amber-700 mb-2">Partnership Review</p>
                <h1 className="text-3xl font-serif font-bold mb-2">Partnership Agreement Checklist</h1>
                <p className="text-sm text-slate-600">Review each item together before signing a final legal agreement.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-7 text-sm">
                <label className="flex items-end gap-2"><span className="font-semibold whitespace-nowrap">Business Name:</span><input className={blankLine} data-testid="agreement-business-name" /></label>
                <label className="flex items-end gap-2"><span className="font-semibold whitespace-nowrap">Date:</span><input className={blankLine} data-testid="agreement-date" /></label>
                <label className="flex items-end gap-2"><span className="font-semibold whitespace-nowrap">Partner A:</span><input className={blankLine} data-testid="agreement-partner-a" /></label>
                <label className="flex items-end gap-2"><span className="font-semibold whitespace-nowrap">Partner B:</span><input className={blankLine} data-testid="agreement-partner-b" /></label>
              </div>

              <div className="space-y-3 mb-7">
                {PARTNER_RULES.map((rule, index) => (
                  <label key={rule.title} className="flex items-start gap-3 p-3 border border-slate-300 rounded-sm" data-testid={`agreement-rule-${index + 1}`}>
                    <input
                      type="checkbox"
                      checked={checked[index]}
                      onChange={() => toggleRule(index)}
                      className="mt-1 w-4 h-4 accent-amber-600"
                      data-testid={`agreement-check-${index + 1}`}
                    />
                    <span>
                      <span className="block font-semibold text-sm">{index + 1}. {rule.title}</span>
                      <span className="block text-xs text-slate-600 mt-1">{rule.review}</span>
                    </span>
                  </label>
                ))}
              </div>

              <div className="mb-7" data-testid="agreement-terms">
                <h2 className="font-serif font-bold text-xl mb-3">Agreed Terms</h2>
                <div className="space-y-5 text-sm">
                  {[
                    ["Investment and ownership split", "agreement-term-investment"],
                    ["Responsibilities of each partner", "agreement-term-responsibilities"],
                    ["Decision making and spending limits", "agreement-term-decisions"],
                    ["Business expenses and record keeping", "agreement-term-expenses"],
                    ["Profit sharing", "agreement-term-profits"],
                    ["How disagreements will be resolved", "agreement-term-disputes"],
                    ["How a partner can exit the business", "agreement-term-exit"]
                  ].map(([label, testId]) => (
                    <label key={testId} className="block">
                      <span className="font-semibold">{label}:</span>
                      <input className={`${blankLine} block w-full`} data-testid={testId} />
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
                {["Partner A", "Partner B"].map(partner => (
                  <div key={partner} className="space-y-5" data-testid={`agreement-signature-${partner.toLowerCase().replace(" ", "-")}`}>
                    <p className="font-semibold text-sm">{partner}</p>
                    <div className="border-b border-slate-500 h-8" />
                    <div className="flex justify-between text-[11px] text-slate-500">
                      <span>Signature</span>
                      <span>Date</span>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-[10px] text-slate-500 text-center">
                This checklist is a business review template, not legal advice. Have a qualified professional review the final partnership agreement.
              </p>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PartnerGuideModal;
